import { Fragment, useMemo, type ReactNode } from 'react'
import { motion } from 'motion/react'
import {
  BarChart3,
  HelpCircle,
  Loader2,
  Moon,
  PackageSearch,
  Pencil,
  Receipt,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Users,
  Volume2,
} from 'lucide-react'
import type { AIActionCardOpen } from '@/types/ai'
import type { CardOp, ChatActionCard } from '@/lib/aiActionCard'
import type { CopilotCapability } from '@/lib/aiCapabilities'
import type { PromptIcon } from '@/lib/aiScreenPrompts'
import { cn } from '@/lib/utils'
import { ActionCard } from './ActionCard'
import { MessageAttachments } from './attachmentViews'

// ── Bhookly AI chat: shared message types and rows ──────────────────────────
// Used by the bubble (BhooklyAI.tsx) and the full page (BhooklyAIPage.tsx),
// so a reply looks the same wherever it is read. No data access here.

export type Role = 'user' | 'assistant'

export interface ChatNote {
  tone: 'ok' | 'muted'
  text: string
}

/** A file a user message carried (F-AI-62). */
export interface ChatAttachment {
  /** Server ai_attachments id. */
  id: string
  name: string
  kind: 'image' | 'pdf' | 'text'
  sizeBytes: number
  pages?: number
  /** This device's copy of an image just sent; restored messages fetch theirs. */
  localUrl?: string
}

export interface ChatMessage {
  /** Local key. */
  id: string
  role: Role
  content: string
  /** Server ai_messages id of an assistant reply — thumbs need it. */
  serverId?: string
  pending?: boolean
  /** A widget-written line (an error or status), not a model reply. */
  system?: boolean
  feedback?: 1 | -1
  note?: ChatNote
  /** Confirm cards the reply proposed (assistant only). */
  actions?: ChatActionCard[]
  /** Files the staff member attached (user only). */
  attachments?: ChatAttachment[]
}

export const BRAND_GRADIENT = 'bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500'

// Starter prompts come from src/ai/screen-prompts.json per screen (F-AI-50);
// this maps each prompt's icon key to a glyph.
export const PROMPT_ICONS: Record<PromptIcon, ReactNode> = {
  chart: <BarChart3 className="h-4 w-4" aria-hidden />,
  stock: <PackageSearch className="h-4 w-4" aria-hidden />,
  receipt: <Receipt className="h-4 w-4" aria-hidden />,
  users: <Users className="h-4 w-4" aria-hidden />,
  moon: <Moon className="h-4 w-4" aria-hidden />,
  help: <HelpCircle className="h-4 w-4" aria-hidden />,
  edit: <Pencil className="h-4 w-4" aria-hidden />,
}

/**
 * What the copilot can do for this role (lib/aiCapabilities.ts), on the empty
 * chat. A chip seeds its example into the composer; it never sends.
 */
export function CapabilityChips({
  items,
  onPick,
  disabled,
}: {
  items: CopilotCapability[]
  onPick: (example: string) => void
  disabled?: boolean
}) {
  if (!items.length) return null
  return (
    <div className="w-full">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">What I can do for you</p>
      <div className="flex flex-wrap justify-center gap-1.5">
        {items.map((c) => (
          <button
            type="button"
            key={c.id}
            onClick={() => onPick(c.example)}
            disabled={disabled}
            title={`Try: ${c.example.trim()}…`}
            className="flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-primary/10 disabled:opacity-50"
          >
            <span className="text-primary [&>svg]:h-3.5 [&>svg]:w-3.5">{PROMPT_ICONS[c.icon]}</span>
            {c.title}
          </button>
        ))}
      </div>
    </div>
  )
}

// Cycle under the typing dots while a turn is in flight: a turn can make a few
// model and data round-trips, and a static cue reads as stuck.
export const PENDING_LABELS = ['Thinking…', 'Pulling the numbers…', 'One second…', 'Checking the data…']

/** POST /ai/tts refuses longer text (backend maxSpeakChars). */
export const MAX_SPEAK_CHARS = 1500

/** A reply as it should be read aloud: the Markdown markers dropped. */
export function speakableText(content: string): string {
  return content
    .replace(/\*\*|`/g, '')
    .replace(/^\s*(#{1,6}|[-*•]|\d+[.)])\s+/gm, '')
    .trim()
}

export const formatElapsed = (totalSeconds: number) =>
  `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`

// ── Message rendering ────────────────────────────────────────────────────────

/** **bold** and `code` inside one line. Plain React text — never HTML. */
function renderInline(text: string): ReactNode[] {
  return text
    .split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
    .filter(Boolean)
    .map((part, i) => {
      if (part.length > 4 && part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>
      }
      if (part.length > 2 && part.startsWith('`') && part.endsWith('`')) {
        return (
          <code key={i} className="rounded bg-foreground/10 px-1 py-0.5 text-[0.85em]">
            {part.slice(1, -1)}
          </code>
        )
      }
      return <Fragment key={i}>{part}</Fragment>
    })
}

type Block = { kind: 'text'; heading: boolean; lines: string[] } | { kind: 'list'; ordered: boolean; items: string[] }

/**
 * The small Markdown subset replies use: paragraphs, headings, bullet and
 * numbered lists, bold and inline code. Anything else renders as text.
 */
export function MessageBody({ content }: { content: string }) {
  const blocks = useMemo(() => {
    const out: Block[] = []
    for (const raw of content.replace(/\r\n/g, '\n').split('\n')) {
      const line = raw.trimEnd()
      const last = out[out.length - 1]
      const bullet = /^\s*[-*•]\s+(.*)$/.exec(line)
      const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line)
      const heading = /^#{1,6}\s+(.*)$/.exec(line)
      const item = bullet?.[1] ?? numbered?.[1]
      if (!line.trim()) {
        out.push({ kind: 'text', heading: false, lines: [] })
      } else if (item !== undefined) {
        const ordered = !bullet
        if (last?.kind === 'list' && last.ordered === ordered) last.items.push(item)
        else out.push({ kind: 'list', ordered, items: [item] })
      } else if (heading) {
        out.push({ kind: 'text', heading: true, lines: [heading[1]] })
      } else if (last?.kind === 'text' && !last.heading) {
        last.lines.push(line)
      } else {
        out.push({ kind: 'text', heading: false, lines: [line] })
      }
    }
    return out.filter((b) => (b.kind === 'text' ? b.lines.length > 0 : b.items.length > 0))
  }, [content])

  return (
    <div className="space-y-1.5 break-words text-sm leading-relaxed">
      {blocks.map((b, i) => {
        if (b.kind === 'list') {
          const List = b.ordered ? 'ol' : 'ul'
          return (
            <List key={i} className={cn('space-y-0.5 pl-4', b.ordered ? 'list-decimal' : 'list-disc')}>
              {b.items.map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </List>
          )
        }
        return (
          <p key={i} className={cn(b.heading && 'font-semibold')}>
            {b.lines.map((l, j) => (
              <Fragment key={j}>
                {j > 0 && <br />}
                {renderInline(l)}
              </Fragment>
            ))}
          </p>
        )
      })}
    </div>
  )
}

export function Orb({ size = 28 }: { size?: number }) {
  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center rounded-full text-white shadow-sm', BRAND_GRADIENT)}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <Sparkles style={{ width: size * 0.55, height: size * 0.55 }} />
    </span>
  )
}

export function TypingDots({ label }: { label?: string }) {
  return (
    <div className="flex flex-col gap-1 py-1" role="status" aria-label="Bhookly AI is answering">
      <div className="flex items-center gap-1" aria-hidden>
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60"
            animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
          />
        ))}
      </div>
      {label && <span className="text-[11px] text-muted-foreground">{label}</span>}
    </div>
  )
}

export function AssistantRow({
  message,
  pendingLabel,
  canSpeak,
  voiceStatus,
  onSpeak,
  onFeedback,
  onCardAction,
  onOpenScreen,
  onAsk,
}: {
  message: ChatMessage
  pendingLabel?: string
  canSpeak: boolean
  voiceStatus?: 'loading' | 'playing'
  onSpeak: (m: ChatMessage) => void
  onFeedback: (m: ChatMessage, thumbs: 1 | -1) => void
  onCardAction: (messageId: string, cardId: string, op: CardOp) => void
  onOpenScreen: (open: AIActionCardOpen) => Promise<string | null>
  /** Sends a card's follow-up question as the next message. */
  onAsk?: (text: string) => void
}) {
  const showTools = !message.pending && !message.system && !!message.content
  // A restored reply can be cards only; it then has no bubble.
  const showBubble = message.pending || message.system || !!message.content || !message.actions?.length
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="group flex items-start gap-2.5"
    >
      <Orb size={28} />
      <div className="min-w-0 flex-1">
        {showBubble && (
          <div
            className={cn(
              'inline-block max-w-full rounded-2xl rounded-tl-md px-3.5 py-2.5 text-foreground',
              message.system ? 'border border-border bg-background text-muted-foreground' : 'bg-muted',
            )}
          >
            {message.pending ? <TypingDots label={pendingLabel} /> : <MessageBody content={message.content} />}
          </div>
        )}
        {message.actions?.map((card) => (
          <ActionCard
            key={card.id}
            card={card}
            onAct={(op) => onCardAction(message.id, card.id, op)}
            onOpen={onOpenScreen}
            onAsk={onAsk}
          />
        ))}
        {message.note && (
          <p
            className={cn(
              'mt-1 text-[12px]',
              message.note.tone === 'ok' ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground',
            )}
          >
            {message.note.text}
          </p>
        )}
        {showTools && (
          <div
            className={cn(
              'mt-1 flex items-center gap-1 opacity-60 transition-opacity focus-within:opacity-100 group-hover:opacity-100',
              (voiceStatus || message.feedback) && 'opacity-100',
            )}
          >
            {canSpeak && speakableText(message.content).length <= MAX_SPEAK_CHARS && (
              <button
                type="button"
                onClick={() => onSpeak(message)}
                aria-label={voiceStatus === 'playing' ? 'Stop reading aloud' : 'Read this reply aloud'}
                className={cn(
                  'rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground',
                  voiceStatus && 'text-primary',
                )}
              >
                {voiceStatus === 'loading' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Volume2 className="h-3.5 w-3.5" aria-hidden />
                )}
              </button>
            )}
            {message.serverId &&
              ([1, -1] as const).map((t) => {
                const Icon = t === 1 ? ThumbsUp : ThumbsDown
                return (
                  <button
                    type="button"
                    key={t}
                    onClick={() => onFeedback(message, t)}
                    aria-label={t === 1 ? 'Helpful' : 'Not helpful'}
                    aria-pressed={message.feedback === t}
                    className={cn(
                      'rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground',
                      message.feedback === t && 'text-primary',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" aria-hidden />
                  </button>
                )
              })}
          </div>
        )}
      </div>
    </motion.div>
  )
}

export function UserRow({ message }: { message: ChatMessage }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="flex flex-col items-end gap-1.5"
    >
      {message.attachments?.length ? <MessageAttachments attachments={message.attachments} /> : null}
      {message.content && (
        <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary px-3.5 py-2.5 text-sm text-primary-foreground shadow-sm">
          {message.content}
        </div>
      )}
    </motion.div>
  )
}
